import React, { useState, useEffect, useRef } from "react";
import {
  X,
  AlertCircle,
  Plus,
  Trash2,
  Calendar,
  Clock,
  MapPin,
  ShieldAlert,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import api from "../../../connector/api";

const INFRACTION_LEVELS = [
  { value: "MINOR", label: "Minor (Classroom Managed - Repeat Offense)" },
  { value: "MAJOR", label: "Major (Office Managed - Immediate Escalation)" },
];

const PREDEFINED_VIOLATIONS = [
  { value: "Classroom Disruption", label: "Classroom Disruption" },
  { value: "Unauthorized Phone/Device Use", label: "Unauthorized Device Use" },
  { value: "Insubordination / Disrespect", label: "Insubordination / Disrespect" },
  { value: "Academic Dishonesty / Cheating", label: "Academic Dishonesty" },
  { value: "Chronic Tardiness", label: "Chronic Tardiness / Truancy" },
  { value: "Property Damage / Vandalism", label: "Property Damage / Vandalism" },
  { value: "OTHER", label: "Other (Specify Custom Offense)" },
];

const INTERVENTION_TYPES = [
  { value: "VERBAL_WARNING", label: "Verbal Warning / Redirection" },
  { value: "SEAT_REASSIGNMENT", label: "Seat Reassignment" },
  { value: "TEACHER_CONFERENCE", label: "Private Student-Teacher Conference" },
  { value: "PARENT_CONTACT", label: "Parent Contact / Email Sent" },
  { value: "THINK_SHEET", label: "Behavior Reflection Sheet" },
  { value: "OTHER", label: "Other Intervention" },
];

/* Custom Dropdown Component */
const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-9 border border-zinc-300 rounded-lg px-3 text-xs bg-white text-zinc-800 flex items-center justify-between hover:border-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors cursor-pointer"
      >
        <span className="truncate font-medium">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0 ml-1" />
      </button>

      {isOpen && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-xl max-h-48 overflow-y-auto py-1 text-xs">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-zinc-100 transition-colors cursor-pointer ${
                value === opt.value
                  ? "bg-blue-50 text-blue-700 font-semibold"
                  : "text-zinc-700"
              }`}
            >
              <span className="truncate">{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DisciplinaryReferralModal = ({
  isOpen,
  onClose,
  selectedStudent,
  selectedRecord: propSelectedRecord,
  initialData, // Added initialData prop alias
  studentId: propStudentId,
  classroomId,
  subjectAssignmentId,
  classId,
  currentPeriodId,
  onSuccess,
  onSubmit,
  submitting = false,
}) => {
  // Normalize record data source
  const selectedRecord = propSelectedRecord || initialData;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dateOfIncident, setDateOfIncident] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [timeOfIncident, setTimeOfIncident] = useState(
    new Date().toTimeString().slice(0, 5)
  );
  const [location, setLocation] = useState("Classroom");
  const [infractionLevel, setInfractionLevel] = useState("MINOR");

  const [selectedViolationOption, setSelectedViolationOption] = useState(
    "Classroom Disruption"
  );
  const [customViolation, setCustomViolation] = useState("");
  const [incidentDescription, setIncidentDescription] = useState("");

  const [interventions, setInterventions] = useState([
    {
      intervention_type: "VERBAL_WARNING",
      date_attempted: new Date().toISOString().split("T")[0],
      other_details: "",
    },
  ]);

  const isEditMode = Boolean(selectedRecord);

  const currentStudent =
    selectedRecord?.student_detail ||
    selectedRecord?.student ||
    selectedStudent?.StudentDetail ||
    selectedStudent?.student ||
    selectedStudent;

  const activeStudentId =
    currentStudent?.id ||
    currentStudent?.student_id ||
    selectedRecord?.student ||
    propStudentId ||
    "";

  const targetClassroom = classroomId || classId || selectedRecord?.classroom;

  // Reset or initialize state on modal opening or selectedRecord changes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIncidentDescription("");
      setDateOfIncident(new Date().toISOString().split("T")[0]);
      setTimeOfIncident(new Date().toTimeString().slice(0, 5));
      setLocation("Classroom");
      setInfractionLevel("MINOR");
      setSelectedViolationOption("Classroom Disruption");
      setCustomViolation("");
      setInterventions([
        {
          intervention_type: "VERBAL_WARNING",
          date_attempted: new Date().toISOString().split("T")[0],
          other_details: "",
        },
      ]);
      return;
    }

    if (selectedRecord) {
      setDateOfIncident(
        selectedRecord.date_of_incident ||
          new Date().toISOString().split("T")[0]
      );
      setTimeOfIncident(
        selectedRecord.time_of_incident ||
          new Date().toTimeString().slice(0, 5)
      );
      setLocation(selectedRecord.location || "Classroom");
      setInfractionLevel(selectedRecord.infraction_level || selectedRecord.severity || "MINOR");
      
      const descriptionText = 
        selectedRecord.incident_description || 
        selectedRecord.description || 
        selectedRecord.notes || 
        "";
      setIncidentDescription(descriptionText);

      const existingViolation = 
        selectedRecord.violation_type || 
        selectedRecord.violation_type_name || 
        selectedRecord.category || 
        "";

      const isPredefined = PREDEFINED_VIOLATIONS.some(
        (opt) => opt.value === existingViolation && opt.value !== "OTHER"
      );

      if (isPredefined) {
        setSelectedViolationOption(existingViolation);
        setCustomViolation("");
      } else if (existingViolation) {
        setSelectedViolationOption("OTHER");
        setCustomViolation(existingViolation);
      } else {
        setSelectedViolationOption("Classroom Disruption");
        setCustomViolation("");
      }

      if (
        Array.isArray(selectedRecord.interventions) &&
        selectedRecord.interventions.length > 0
      ) {
        setInterventions(
          selectedRecord.interventions.map((item) => ({
            intervention_type: item.intervention_type || "VERBAL_WARNING",
            date_attempted:
              item.date_attempted ||
              item.intervention_date ||
              new Date().toISOString().split("T")[0],
            other_details: item.other_details || "",
          }))
        );
      }
    }
  }, [isOpen, selectedRecord]);

  if (!isOpen) return null;

  const handleAddIntervention = () => {
    setInterventions((prev) => [
      ...prev,
      {
        intervention_type: "VERBAL_WARNING",
        date_attempted: new Date().toISOString().split("T")[0],
        other_details: "",
      },
    ]);
  };

  const handleRemoveIntervention = (index) => {
    setInterventions((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleInterventionChange = (index, field, value) => {
    setInterventions((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!activeStudentId) {
      setError("No student selected for this referral.");
      return;
    }

    const finalViolationType =
      selectedViolationOption === "OTHER"
        ? customViolation.trim()
        : selectedViolationOption;

    if (!finalViolationType) {
      setError("Please specify the violation type.");
      return;
    }

    setError(null);

    const payload = {
      student: activeStudentId,
      classroom: targetClassroom,
      subject_assignment: subjectAssignmentId || null,
      school_year_term: currentPeriodId || null,
      date_of_incident: dateOfIncident,
      time_of_incident: timeOfIncident,
      location,
      infraction_level: infractionLevel,
      violation_type: finalViolationType,
      incident_description: incidentDescription,
      interventions: interventions.map((item) => ({
        intervention_type: item.intervention_type,
        date_attempted: item.date_attempted || null,
        other_details:
          item.intervention_type === "OTHER" ? item.other_details : "",
      })),
    };

    if (typeof onSubmit === "function") {
      try {
        await onSubmit(payload);
        if (onSuccess) onSuccess();
        onClose();
      } catch (err) {
        const backendErrors = err?.response?.data;
        if (typeof backendErrors === "object" && backendErrors !== null) {
          const firstKey = Object.keys(backendErrors)[0];
          const firstMsg = Array.isArray(backendErrors[firstKey])
            ? backendErrors[firstKey][0]
            : backendErrors[firstKey];
          setError(`${firstKey}: ${firstMsg}`);
        } else {
          setError(err?.message || "Failed to save referral.");
        }
      }
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await api.patch(
          `teacher/discipline-referrals/${selectedRecord.id}/`,
          payload
        );
      } else {
        await api.post("teacher/discipline-referrals/", payload);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Failed to save referral:", err);
      const backendErrors = err.response?.data;
      if (typeof backendErrors === "object" && backendErrors !== null) {
        const firstErrorKey = Object.keys(backendErrors)[0];
        const firstErrorMsg = Array.isArray(backendErrors[firstErrorKey])
          ? backendErrors[firstErrorKey][0]
          : backendErrors[firstErrorKey];
        setError(`${firstErrorKey}: ${firstErrorMsg}`);
      } else {
        setError(
          err.response?.data?.detail || "Failed to record disciplinary action."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const isProcessing = loading || submitting;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white border border-zinc-200 rounded-xl shadow-2xl w-full max-w-3xl my-auto overflow-hidden text-xs flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 bg-zinc-50/80">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg text-amber-700">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-zinc-900 text-sm">
                {isEditMode ? "Edit Incident Referral" : "Behavior Incident Referral"}
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-200/60 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form
          onSubmit={handleSubmitForm}
          className="p-5 overflow-y-auto space-y-4 flex-1"
        >
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg flex items-center gap-2 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Incident Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block font-medium text-zinc-700 mb-1 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-zinc-400" /> Date
              </label>
              <input
                type="date"
                required
                value={dateOfIncident}
                onChange={(e) => setDateOfIncident(e.target.value)}
                className="w-full h-9 border border-zinc-300 rounded-lg px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
              />
            </div>

            <div>
              <label className="block font-medium text-zinc-700 mb-1 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-zinc-400" /> Time
              </label>
              <input
                type="time"
                required
                value={timeOfIncident}
                onChange={(e) => setTimeOfIncident(e.target.value)}
                className="w-full h-9 border border-zinc-300 rounded-lg px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
              />
            </div>

            <div>
              <label className="block font-medium text-zinc-700 mb-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-zinc-400" /> Location
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Room 104, Gymnasium"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full h-9 border border-zinc-300 rounded-lg px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
              />
            </div>
          </div>

          {/* Level & Violation Options */}
          <div className="space-y-3">
            <div>
              <label className="block font-medium text-zinc-700 mb-1">
                Infraction Level
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {INFRACTION_LEVELS.map((level) => (
                  <label
                    key={level.value}
                    className={`p-2.5 border rounded-lg flex items-center gap-2 cursor-pointer transition-colors ${
                      infractionLevel === level.value
                        ? "border-blue-500 bg-blue-50/50 text-blue-900 font-semibold"
                        : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="infraction_level"
                      value={level.value}
                      checked={infractionLevel === level.value}
                      onChange={(e) => setInfractionLevel(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>{level.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-medium text-zinc-700 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-zinc-400" /> Violation Category
              </label>
              <CustomDropdown
                options={PREDEFINED_VIOLATIONS}
                value={selectedViolationOption}
                onChange={(val) => setSelectedViolationOption(val)}
                className="w-full"
              />
            </div>

            {selectedViolationOption === "OTHER" && (
              <div className="pt-1">
                <label className="block font-medium text-zinc-700 mb-1">
                  Specify Custom Violation *
                </label>
                <input
                  type="text"
                  required
                  placeholder='e.g., "Left campus without authorization"'
                  value={customViolation}
                  onChange={(e) => setCustomViolation(e.target.value)}
                  className="w-full h-9 border border-zinc-300 rounded-lg px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                />
              </div>
            )}
          </div>

          {/* Interventions Section */}
          <div className="space-y-2 border-t border-zinc-200 pt-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-zinc-800">
                Classroom Interventions Attempted
              </span>
              <button
                type="button"
                onClick={handleAddIntervention}
                className="text-blue-700 hover:text-blue-900 font-semibold flex items-center gap-1 text-[11px] cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" /> Add Intervention
              </button>
            </div>

            {interventions.map((item, index) => (
              <div
                key={index}
                className="p-3 border border-zinc-200 rounded-lg bg-zinc-50/50 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <CustomDropdown
                    options={INTERVENTION_TYPES}
                    value={item.intervention_type}
                    onChange={(val) =>
                      handleInterventionChange(index, "intervention_type", val)
                    }
                    className="flex-1"
                  />

                  <input
                    type="date"
                    value={item.date_attempted}
                    onChange={(e) =>
                      handleInterventionChange(
                        index,
                        "date_attempted",
                        e.target.value
                      )
                    }
                    className="h-9 border border-zinc-300 rounded-lg px-2.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />

                  {interventions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveIntervention(index)}
                      className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {item.intervention_type === "OTHER" && (
                  <input
                    type="text"
                    placeholder="Specify details for this intervention..."
                    value={item.other_details}
                    onChange={(e) =>
                      handleInterventionChange(
                        index,
                        "other_details",
                        e.target.value
                      )
                    }
                    className="w-full h-9 border border-zinc-300 rounded-lg px-2.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Detailed Narrative */}
          <div className="space-y-1 border-t border-zinc-200 pt-3">
            <label className="block font-bold text-zinc-800">
              Factual Incident Description
            </label>
            <p className="text-[11px] text-zinc-500">
              Provide an objective narrative of events and immediate classroom resolution actions.
            </p>
            <textarea
              required
              rows={4}
              value={incidentDescription}
              onChange={(e) => setIncidentDescription(e.target.value)}
              placeholder="Detailed description of what occurred..."
              className="w-full border border-zinc-300 rounded-lg p-2.5 text-xs text-zinc-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
            />
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="h-9 px-4 border border-zinc-300 text-zinc-700 hover:bg-zinc-100 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="h-9 px-5 bg-blue-900 hover:bg-blue-950 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer shadow-sm"
            >
              {isProcessing
                ? isEditMode
                  ? "Updating..."
                  : "Submitting..."
                : isEditMode
                ? "Update Referral"
                : "Submit Referral"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DisciplinaryReferralModal;